// src/app/modules/catalogues/CatalogueManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, X, Trash2, Edit, BookOpen, Search, Check,
    Image as ImageIcon, FileText, ExternalLink, Filter
} from "lucide-react";
import {
    fetchCataloguesRequest,
    createCatalogueRequest,
    updateCatalogueRequest,
    deleteCatalogueRequest,
} from "../../../../redux/actions/cataloguesActions";
import { fetchFormationsRequest } from "../../../../redux/actions/formationsActions";

import "../RessourcesStyles/CatalogueManagement.css";
import {API_BASE_URL} from "../../../../../api";

const emptyForm = {
    nom: "",
    reference: "",
    description: "",
    status: 1,
    formationIds: [],
    imageFile: null,
    file: null,
};

export default function CatalogueManagement() {
    const dispatch = useDispatch();

    const {
        catalogues,
        loading,
    } = useSelector((state) => state.catalogues || {});
    const formations = useSelector((state) => state.formations.list || {});

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const [form, setForm] = useState(emptyForm);
    const [tableQ, setTableQ] = useState("");
    const [searchFormation, setSearchFormation] = useState("");

    const getImageUrl = (filename) => filename ? `${API_BASE_URL}/api/catalogues/images/${filename}` : null;
    const getFileUrl  = (filename) => filename ? `${API_BASE_URL}/api/catalogues/files/${filename}` : null;

    useEffect(() => {
        dispatch(fetchCataloguesRequest());
        dispatch(fetchFormationsRequest());
    }, [dispatch]);

    const filteredCatalogues = useMemo(() => {
        const q = tableQ.trim().toLowerCase();
        if (!q) return catalogues || [];
        return (catalogues || []).filter(c =>
            `${c.nom || ""} ${c.reference || ""}`.toLowerCase().includes(q)
        );
    }, [catalogues, tableQ]);

    const formationsFiltered = useMemo(() => {
        const q = (searchFormation || "").toLowerCase().trim();
        if (!q) return formations || [];
        return (formations || []).filter((f) =>
            `${f.reference || ""} ${f.description || ""}`.toLowerCase().includes(q)
        );
    }, [formations, searchFormation]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (c) => {
        setIsEdit(true);
        setCurrentId(c.id);
        setForm({
            nom: c.nom || "",
            reference: c.reference || "",
            description: c.description || "",
            status: c.status ?? 1,
            formationIds: c.formationIds || [],
            imageFile: null,
            file: null,
            picture: c.picture || null,
            attachment: c.attachment || null,
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
        setSearchFormation("");
    };

    const toggleFormation = (id) => {
        setForm((prev) => {
            const exists = (prev.formationIds || []).includes(id);
            const next = exists
                ? prev.formationIds.filter((x) => x !== id)
                : [...prev.formationIds, id];
            return { ...prev, formationIds: next };
        });
    };

    const onSubmit = () => {
        if (!form.reference?.trim()) {
            Swal.fire({ icon: "warning", text: "Référence obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }
        if (!form.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom,
            reference: form.reference,
            description: form.description,
            status: form.status,
            formationIds: form.formationIds || [],
            imageFile: form.imageFile,
            file: form.file,
        };

        if (isEdit && currentId) {
            dispatch(updateCatalogueRequest(currentId, payload));
        } else {
            dispatch(createCatalogueRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteCatalogueRequest(id));
        });
    };

    return (
        <div className="cat-page">
            <div className="cat-header">
                <div>
                    <h2>Catalogues de formation</h2>
                    <p>Gérez vos catalogues et leurs formations associées</p>
                </div>
                <button className="cat-btn cat-btn-primary" onClick={openAdd}>
                    <Plus size={15} /> Ajouter Catalogue
                </button>
            </div>

            <div className="cat-toolbar">
                <div className="cat-search-wrap">
                    <Search size={14} className="cat-search-icon" />
                    <input
                        type="text"
                        className="cat-search"
                        value={tableQ}
                        onChange={e => setTableQ(e.target.value)}
                        placeholder="Rechercher par nom ou référence..."
                    />
                </div>
                <div style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                    {loading ? "..." : `${filteredCatalogues.length} catalogue(s)`}
                </div>
            </div>

            <div className="cat-card">
                <div className="cat-table-wrap">
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Référence</th>
                                <th>Status</th>
                                <th>Formations</th>
                                <th style={{ textAlign: "right", width: 120 }}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="cat-loading">Chargement...</td>
                                </tr>
                            ) : filteredCatalogues.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: "center", padding: 32, opacity: 0.6, fontSize: 12 }}>
                                        Aucun catalogue trouvé
                                    </td>
                                </tr>
                            ) : (
                                filteredCatalogues.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <BookOpen size={14} style={{ color: "#ee645f" }} />
                                                <span style={{ fontWeight: 700, color: "#204385" }}>{c.nom}</span>
                                            </div>
                                        </td>
                                        <td><span className="cat-badge cat-badge--blue">{c.reference}</span></td>
                                        <td>
                                            <span className={`cat-pill ${c.status === 1 ? "ok" : "off"}`}>
                                                {c.status === 1 ? "Actif" : "Inactif"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="cat-badge cat-badge--grey">
                                                {(c.formationIds || []).length} formation(s)
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div className="cat-actions">
                                                <button className="cat-icon-btn" title="Modifier" onClick={() => openEdit(c)}>
                                                    <Edit size={15} />
                                                </button>
                                                <button className="cat-icon-btn cat-icon-btn--danger" title="Supprimer" onClick={() => onDelete(c.id)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL */}
            {open && (
                <div className="cat-modal-overlay" onClick={closeModal}>
                    <div className="cat-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>
                                <BookOpen size={16} style={{ color: "#ee645f" }} />
                                {isEdit ? "Modifier Catalogue" : "Ajouter Catalogue"}
                            </h3>
                            <button className="cat-x" onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className="cat-modal-body">
                            <div className="cat-grid">
                                <div className="cat-field">
                                    <label>Nom *</label>
                                    <input
                                        value={form.nom}
                                        onChange={(e) => setForm({ ...form, nom: e.target.value })}
                                        placeholder="Nom catalogue"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Référence *</label>
                                    <input
                                        value={form.reference}
                                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                        placeholder="CAT-2026"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Status</label>
                                    <select value={form.status} onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
                                        <option value={1}>Actif</option>
                                        <option value={0}>Inactif</option>
                                    </select>
                                </div>

                                <div className="cat-field cat-span-2">
                                    <label>Description</label>
                                    <textarea
                                        rows={3}
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        placeholder="Description..."
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Image (optionnel)</label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {form.picture && !form.imageFile && (
                                            <div className="cat-preview">
                                                <img src={getImageUrl(form.picture)} alt="Catalogue" />
                                                <ImageIcon size={14} style={{ color: "#6b7280" }} />
                                            </div>
                                        )}
                                        {form.imageFile && (
                                            <div className="cat-preview">
                                                <span className="cat-file-pill">Nouvelle image: {form.imageFile.name}</span>
                                            </div>
                                        )}
                                        <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] || null })} />
                                    </div>
                                </div>

                                <div className="cat-field">
                                    <label>Fichier (optionnel)</label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {form.attachment && !form.file && (
                                            <div className="cat-doc">
                                                <a href={getFileUrl(form.attachment)} target="_blank" rel="noreferrer">
                                                    <FileText size={14} style={{ verticalAlign: "middle", marginRight: 5 }} />
                                                    Document actuel
                                                </a>
                                            </div>
                                        )}
                                        {form.file && (
                                            <div className="cat-doc">
                                                <span className="cat-file-pill">Nouveau fichier: {form.file.name}</span>
                                            </div>
                                        )}
                                        <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
                                    </div>
                                </div>
                            </div>

                            <div className="cat-divider" />

                            <div className="cat-formations">
                                <div className="cat-formations-head">
                                    <h4>Sélection des formations</h4>
                                    <div className="cat-search-wrap" style={{ width: 280 }}>
                                        <Search size={13} className="cat-search-icon" />
                                        <input
                                            className="cat-search"
                                            value={searchFormation}
                                            onChange={(e) => setSearchFormation(e.target.value)}
                                            placeholder="Rechercher formation..."
                                        />
                                    </div>
                                </div>

                                <div className="cat-formations-table-wrap">
                                    <table className="cat-formations-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 40, textAlign: "center" }}>
                                                    <Check size={13} />
                                                </th>
                                                <th style={{ width: 100 }}>Référence</th>
                                                <th>Formation</th>
                                                <th>Domaine / Thème</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(formationsFiltered || []).map((f) => {
                                                const checked = (form.formationIds || []).includes(f.id);
                                                return (
                                                    <tr key={f.id} className={checked ? "selected" : ""} onClick={() => toggleFormation(f.id)} style={{ cursor: "pointer" }}>
                                                        <td style={{ textAlign: "center" }}>
                                                            <div className={`cat-checkbox ${checked ? "checked" : ""}`} style={{ margin: "auto" }}>
                                                                {checked && <Check size={10} />}
                                                            </div>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => {}} // click handled by tr
                                                                style={{ display: "none" }}
                                                            />
                                                        </td>
                                                        <td><span className="cat-badge cat-badge--blue">{f.reference}</span></td>
                                                        <td>
                                                            <div style={{ fontWeight: 600 }}>{f.description}</div>
                                                        </td>
                                                        <td>
                                                            <div className="cat-selected" style={{ marginTop: 0 }}>
                                                                {f.domaineNom || f.domaine?.nom || "-"}
                                                                <span style={{ margin: "0 4px", opacity: 0.5 }}>/</span>
                                                                {f.themeNom || f.theme?.nom || "-"}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="cat-selected">
                                    Formations sélectionnées : <b>{(form.formationIds || []).length}</b>
                                </div>
                            </div>
                        </div>

                        <div className="cat-modal-footer">
                            <button className="cat-btn cat-btn-light" onClick={closeModal}>Annuler</button>
                            <button className="cat-btn cat-btn-primary" onClick={onSubmit}>
                                {isEdit ? "Enregistrer" : "Ajouter"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
